import { useState, useCallback, useRef, useEffect } from 'react';
import shp from 'shpjs';

const useLayerManagement = () => {
    // 레이어 상태 및 선택 관리
    const [layers, setLayers] = useState([]);
    const [selectedLayerIndex, setSelectedLayerIndex] = useState(null);
    const [attributeExpandSignal, setAttributeExpandSignal] = useState(0);

    // 모달 및 파일 관련 상태
    const fileInputRef = useRef();
    const [isUploadModalOpen, setUploadModalOpen] = useState(false);
    const [isDragActive, setDragActive] = useState(false);

    // ------------ 레이어 관리 ------------
    const addLayer = useCallback((geojson, name = `Layer ${layers.length + 1}`) => {
        setLayers((prev) => {
            const newLayer = {
                name,
                geojson,
                visible: true,
                group: "Default",
                styles: {
                    point: { fill: "#ff0000", stroke: "#b71c1c", radius: 6, opacity: 0.9 },
                    line: { stroke: "#1565c0", width: 3, opacity: 1 },
                    polygon: { fill: "#2e7d32", stroke: "#1b5e20", width: 2, opacity: 0.85 },
                },
            };
            // setSelectedLayerIndex(prev.length);
            return [...prev, newLayer];
        });
        // 레이어가 추가된 후, 선택 상태를 명시적으로 null로 설정하여 테이블 이름 표시 방지
        // -> 레이어의 테이블 버튼 클릭 시 테이블이 정상적으로 펼쳐지도록 설정
        setSelectedLayerIndex(null);
    }, [layers.length]);

    const updateLayer = useCallback((index, updatedProps) => {
        setLayers((prev) =>
            prev.map((l, i) => (i === index ? { ...l, ...updatedProps } : l))
        );
    }, []);

    const reorderLayers = useCallback((startIndex, endIndex) => {
        const updated = Array.from(layers);
        const [removed] = updated.splice(startIndex, 1);
        updated.splice(endIndex, 0, removed);
        setLayers(updated);
    }, [layers]);

    const removeLayer = useCallback((index) => {
        setLayers((prev) => prev.filter((_, i) => i !== index));
        if (selectedLayerIndex === index) {
            setSelectedLayerIndex(null);
        }
    }, [selectedLayerIndex]);

    // 레이어 목록이 변경될 때마다 선택 상태를 초기화
    useEffect(() => {
        // 레이어 목록이 비어 있으면 선택 상태를 null로 설정
        if (layers.length === 0) {
            setSelectedLayerIndex(null);
        } 
        // 로드된 레이어 목록이 있으나, 현재 selectedIndex가 범위를 벗어난 경우에도 null 처리
        else if (selectedLayerIndex !== null && selectedLayerIndex >= layers.length) {
            setSelectedLayerIndex(null);
        }
    }, [layers, selectedLayerIndex, setSelectedLayerIndex]);


    // ------------ 모달/파일 처리 로직 ------------
    const openUploadModal = useCallback(() => setUploadModalOpen(true), []);
    const closeUploadModal = useCallback(() => setUploadModalOpen(false), []);

    const processFile = useCallback((file) => {
        if (!file) return;
        const fileName = file.name.toLowerCase();

        // GeoJSON/JSON 처리
        if (fileName.endsWith(".geojson") || fileName.endsWith(".json")) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const json = JSON.parse(e.target.result);
                    addLayer(json, file.name);
                    closeUploadModal();
                } catch (err) {
                    console.error("GeoJSON 파싱 실패:", err);
                    alert("지원되지 않는 GeoJSON 파일 형식입니다.");
                }
            };
            reader.readAsText(file);
            return;
        }

        // ZIP/SHP 처리
        if (fileName.endsWith(".zip") || fileName.endsWith(".shp")) {
            const reader = new FileReader();
            reader.onload = (e) => {
                shp(e.target.result)
                    .then((json) => {
                        addLayer(json, file.name);
                        closeUploadModal();
                    })
                    .catch((err) => {
                        console.error("Shapefile 변환 실패:", err);
                        alert("지원되지 않는 Shapefile/ZIP 형식입니다.");
                    });
            };
            reader.readAsArrayBuffer(file);
            return;
        }

        alert("지원되지 않는 파일 형식입니다.");
    }, [addLayer, closeUploadModal]);

    const handleFileUpload = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            processFile(file);
        }
        event.target.value = null;
    };

    const handleClick = () => fileInputRef.current.click();
    const handleDrop = (event) => {
        event.preventDefault();
        setDragActive(false);
        const file = event.dataTransfer.files?.[0];
        processFile(file);
    };
    const handleDragOver = (event) => { event.preventDefault(); setDragActive(true); };
    const handleDragLeave = (event) => { event.preventDefault(); setDragActive(false); };

    const handleDownload2 = () => {
        const link = document.createElement("a");
        link.href = process.env.PUBLIC_URL + "/files/stores.geojson"; 
        link.download = "stores.geojson"; 
        link.click();
    };

    const handleDownload1 = () => {
        const link = document.createElement("a");
        link.href = process.env.PUBLIC_URL + "/files/sido.geojson";
        link.download = "sido.geojson";
        link.click();
    };

    return {
        // 레이어 상태/함수
        layers,
        selectedLayerIndex,
        setSelectedLayerIndex,
        attributeExpandSignal,
        setAttributeExpandSignal,
        updateLayer,
        reorderLayers,
        removeLayer,
        
        // 모달/파일 상태/함수
        fileInputRef,
        isUploadModalOpen,
        isDragActive,
        openUploadModal,
        closeUploadModal,
        handleFileUpload,
        handleClick,
        handleDrop,
        handleDragOver,
        handleDragLeave,
        handleDownload1,
        handleDownload2,
    };
};

export default useLayerManagement;